import { isNil } from 'es-toolkit';

export type PaginationReducerState = {
  readonly pageSize: number;
  readonly currentPage: number;
  readonly startCursor: string | null;
  readonly endCursor: string | null;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
  readonly currentAfter: string | null;
  readonly currentBefore: string | null;
  readonly currentFirst: number | null;
  readonly currentLast: number | null;
};

export type PaginationAction =
  | { type: 'GO_TO_NEXT_PAGE' }
  | { type: 'GO_TO_PREVIOUS_PAGE' }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | {
      type: 'SET_CURSORS';
      payload: { startCursor: string | null; endCursor: string | null; hasNextPage: boolean; hasPreviousPage: boolean };
    }
  | { type: 'RESET'; payload: number }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | {
      type: 'SET_PAGINATION_FROM_URL';
      payload: { after: string | null; before: string | null; pageSize: number; currentPage: number | null };
    };

export function createInitialState(
  pageSize: number,
  currentPage?: number,
  after?: string | null,
  before?: string | null,
): PaginationReducerState {
  const hasBefore = !isNil(before);
  return {
    pageSize,
    currentPage: currentPage ?? 1,
    startCursor: null,
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
    currentAfter: after ?? null,
    currentBefore: before ?? null,
    currentFirst: hasBefore ? null : pageSize,
    currentLast: hasBefore ? pageSize : null,
  };
}

export function paginationReducer(state: PaginationReducerState, action: PaginationAction): PaginationReducerState {
  switch (action.type) {
    case 'GO_TO_NEXT_PAGE':
      if (!state.hasNextPage || isNil(state.endCursor)) {
        return state;
      }
      return {
        ...state,
        currentAfter: state.endCursor,
        currentBefore: null,
        currentFirst: state.pageSize,
        currentLast: null,
        currentPage: state.currentPage + 1,
      };

    case 'GO_TO_PREVIOUS_PAGE':
      if (!state.hasPreviousPage || isNil(state.startCursor)) {
        return state;
      }
      return {
        ...state,
        currentBefore: state.startCursor,
        currentAfter: null,
        currentFirst: null,
        currentLast: state.pageSize,
        currentPage: state.currentPage - 1,
      };

    case 'SET_PAGE_SIZE':
      return {
        ...state,
        pageSize: action.payload,
        currentAfter: null,
        currentBefore: null,
        currentFirst: action.payload,
        currentLast: null,
        currentPage: 1,
        startCursor: null,
        endCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
      };

    case 'SET_CURSORS':
      return {
        ...state,
        startCursor: action.payload.startCursor,
        endCursor: action.payload.endCursor,
        hasNextPage: action.payload.hasNextPage,
        hasPreviousPage: action.payload.hasPreviousPage,
      };

    case 'RESET':
      return createInitialState(action.payload);

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };

    case 'SET_PAGINATION_FROM_URL': {
      const hasBefore = !isNil(action.payload.before);
      return {
        ...state,
        pageSize: action.payload.pageSize,
        currentPage: action.payload.currentPage ?? 1,
        currentAfter: action.payload.after,
        currentBefore: action.payload.before,
        currentFirst: hasBefore ? null : action.payload.pageSize,
        currentLast: hasBefore ? action.payload.pageSize : null,
        startCursor: null,
        endCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    default:
      return state;
  }
}
